import React, { useState, KeyboardEvent } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

interface DomainInputProps {
  value: string[];
  onChange: (domains: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}

const PROTOCOL_REGEX = /^https?:\/\//i;
const DOMAIN_REGEX = /^(\*\.)?[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*$/;

function stripProtocol(input: string): { domain: string; hadProtocol: boolean } {
  const hadProtocol = PROTOCOL_REGEX.test(input);
  const domain = input.replace(PROTOCOL_REGEX, '').replace(/\/+$/, '');
  return { domain, hadProtocol };
}

function isValidDomain(domain: string): boolean {
  if (!domain) return false;
  if (domain === '*') return true; // Allow wildcard for all domains
  return DOMAIN_REGEX.test(domain);
}

export const DomainInput: React.FC<DomainInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder = 'example.com, *.example.com',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [warning, setWarning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const addDomain = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const { domain, hadProtocol } = stripProtocol(trimmed);

    if (hadProtocol) {
      setWarning('Protocol removed - only domain names are needed');
      setTimeout(() => setWarning(null), 3000);
    }

    if (!isValidDomain(domain)) {
      setError('Invalid domain format');
      setTimeout(() => setError(null), 3000);
      return;
    }

    if (value.includes(domain)) {
      setError('Domain already added');
      setTimeout(() => setError(null), 2000);
      return;
    }

    onChange([...value, domain]);
    setInputValue('');
    setError(null);
  };

  const removeDomain = (domainToRemove: string) => {
    onChange(value.filter(d => d !== domainToRemove));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDomain(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
      removeDomain(value[value.length - 1]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    if (pastedText.includes(',')) {
      e.preventDefault();
      const domains = pastedText.split(',').map(d => d.trim()).filter(Boolean);
      domains.forEach(addDomain);
    }
  };

  return (
    <div className="space-y-2">
      <div
        className={`flex flex-wrap gap-1.5 p-2 min-h-10 border rounded-md bg-background ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'focus-within:ring-2 focus-within:ring-ring'
        }`}
      >
        {value.map(domain => (
          <Badge
            key={domain}
            variant="secondary"
            className="gap-1 pr-1"
          >
            {domain}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeDomain(domain)}
                className="ml-1 hover:bg-muted rounded-full p-0.5"
                aria-label={`Remove ${domain}`}
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
        <Input
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={() => inputValue && addDomain(inputValue)}
          disabled={disabled}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] border-0 p-0 h-6 focus-visible:ring-0 shadow-none"
        />
      </div>
      {warning && (
        <p className="text-xs text-warning-500">{warning}</p>
      )}
      {error && (
        <p className="text-xs text-destructive">{error}</p>
      )}
      <p className="text-xs text-muted-foreground">
        Press Enter or comma to add. Wildcards supported (*.example.com)
      </p>
    </div>
  );
};
